import { FC, ReactNode, useEffect, useRef, useState } from 'react'
import {
  Typography,
  Button,
  Box,
  ButtonBase,
  IconButton,
  InputBase,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  AttachFileOutlined,
  Close,
  InfoOutlined,
  Send,
  SupportAgentOutlined
} from '@mui/icons-material'
import { Link, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'
import { MuiMarkdown } from 'mui-markdown'
import Icon from 'components/Icon'
import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import MobileBottomFooter from 'components/MobileBottomFooter'
import FaultTimeline from 'components/Defects/FaultTimeline'
import { getFaultGradient, getFaultStatusColors } from 'components/Helpdesk/helpdesk.utils'
import {
  useHelpdeskTicketsList,
  useHelpdeskTicketsMessagesCreate,
  useHelpdeskTicketsMessagesList
} from 'services/helpdesk/hooks'
import { useCanReportFault } from 'components/Helpdesk/useCanReportFault'

// Fault ticket detail ("Panel Klienta 3.0"): chat-style detail panel on desktop,
// full-screen view with back navigation on mobile.
const Defect: FC = () => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { formatMessage } = useIntl()
  const { id } = useParams()
  const { getTicket } = useHelpdeskTicketsList()
  const { ticketsMessagesList, refetchMessagesList } = useHelpdeskTicketsMessagesList(Number(id))
  const { createMessage } = useHelpdeskTicketsMessagesCreate()
  const { canReportFault } = useCanReportFault()
  const ticket = getTicket(Number(id))

  const [showDetails, setShowDetails] = useState(false)
  const [messageText, setMessageText] = useState('')
  const [attachment, setAttachment] = useState<File | null>(null)
  const [isSending, setIsSending] = useState(false)
  const chatRef = useRef<HTMLDivElement>(null)

  const messagesCount = ticketsMessagesList.length

  // Keep the chat scrolled to the latest message.
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight
    }
  }, [messagesCount, showDetails, id])

  const handleOpenAttachment = (event: any, attachment: string) => {
    event.preventDefault()
    window.open(attachment, '_blank')
  }

  if (!ticket) {
    return null
  }

  const badgeColors = getFaultStatusColors(ticket.status.code, isDesktop)
  const gradient = getFaultGradient(ticket.faultCategory?.id ?? 0)
  const authorName = `${ticket.authorPerson.firstName} ${ticket.authorPerson.lastName}`.trim()
  const authorInitials =
    `${ticket.authorPerson.firstName?.[0] ?? ''}${ticket.authorPerson.lastName?.[0] ?? ''}` || '•'

  const statusBadge = (
    <Box
      sx={{
        backgroundColor: badgeColors.backgroundColor,
        padding: '3px 10px',
        flexShrink: 0
      }}>
      <Typography
        sx={{
          color: badgeColors.color,
          fontSize: '11px',
          fontWeight: 700,
          lineHeight: 'normal',
          whiteSpace: 'nowrap'
        }}>
        {ticket.status.name}
      </Typography>
    </Box>
  )

  const renderAttachment = (attachment: string | null): ReactNode => {
    if (!attachment) return null
    if (attachment.includes('.pdf')) {
      return (
        <Box
          sx={{
            position: 'relative',
            display: 'inline-block',
            maxHeight: '200px',
            maxWidth: '100%',
            marginTop: '10px',
            cursor: 'pointer'
          }}>
          <iframe
            title='attachment'
            src={attachment}
            style={{ height: '100%', width: '100%', border: 'none', overflow: 'hidden' }}
            scrolling='no'
          />
          <Box
            onClick={(event) => handleOpenAttachment(event, attachment)}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'transparent'
            }}
          />
        </Box>
      )
    }
    return (
      <a href={attachment} target='_blank' rel='noopener noreferrer'>
        <Box
          sx={{ maxHeight: '200px', maxWidth: '100%', mt: 1, cursor: 'pointer' }}
          component='img'
          src={attachment}
          alt={ticket.status.name}
        />
      </a>
    )
  }

  const canReply = ticket.status.code !== 'READY' && canReportFault

  if (isDesktop) {
    const chatSeparator = (label: string) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', py: '6px' }}>
        <Box sx={{ flex: 1, height: '1px', backgroundColor: theme.r4r.greyLight }} />
        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: theme.r4r.grey }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1, height: '1px', backgroundColor: theme.r4r.greyLight }} />
      </Box>
    )

    const userAvatar = (
      <Box
        sx={{
          width: '32px',
          height: '32px',
          borderRadius: '16px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: theme.r4r.gradient,
          color: '#fff',
          fontSize: '10px',
          fontWeight: 700
        }}>
        {authorInitials}
      </Box>
    )

    const adminAvatar = (
      <Box
        sx={{
          width: '32px',
          height: '32px',
          borderRadius: '16px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: theme.r4r.gradientViolet,
          color: '#fff'
        }}>
        <SupportAgentOutlined sx={{ fontSize: '16px' }} />
      </Box>
    )

    const renderBubble = (options: {
      key: string
      isMine: boolean
      content: ReactNode
      meta: string
    }) => (
      <Box
        key={options.key}
        sx={{
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-end',
          alignSelf: options.isMine ? 'flex-end' : 'flex-start',
          flexDirection: options.isMine ? 'row-reverse' : 'row',
          maxWidth: '88%'
        }}>
        {options.isMine ? userAvatar : adminAvatar}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: options.isMine ? 'flex-end' : 'flex-start'
          }}>
          <Box
            sx={{
              backgroundColor: options.isMine ? theme.r4r.navy : '#fff',
              color: options.isMine ? '#fff' : theme.r4r.ink,
              boxShadow: options.isMine ? 'none' : '0px 1px 3px rgba(0, 0, 0, 0.06)',
              padding: '12px 16px',
              fontSize: '13px',
              lineHeight: '20.8px'
            }}>
            {options.content}
          </Box>
          <Typography
            sx={{
              fontSize: '10px',
              color: theme.r4r.grey,
              mt: '5px',
              px: '4px',
              textAlign: options.isMine ? 'right' : 'left'
            }}>
            {options.meta}
          </Typography>
        </Box>
      </Box>
    )

    const handleSend = async () => {
      if ((!messageText.trim() && !attachment) || isSending) return
      setIsSending(true)
      try {
        await createMessage({
          message: messageText.trim(),
          attachment,
          ticketId: Number(id)
        })
        await refetchMessagesList()
        setMessageText('')
        setAttachment(null)
      } finally {
        setIsSending(false)
      }
    }

    return (
      <Box
        sx={{
          backgroundColor: '#fff',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.06)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          height: 'calc(100vh - 250px)',
          minHeight: '520px'
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '22px 28px 23px',
            borderBottom: `1px solid ${theme.r4r.greyLight}`
          }}>
          <Box
            sx={{
              width: '52px',
              height: '52px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: gradient,
              flexShrink: 0
            }}>
            <Icon
              name={ticket.faultCategory?.iconName ?? 'QuestionMark'}
              sx={{ color: '#fff', fontSize: '24px' }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: 800,
                color: theme.r4r.navy,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              {ticket.topic}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '4px' }}>
              <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, whiteSpace: 'nowrap' }}>
                {dayjs(ticket.createdAt).format('DD.MM.YYYY')} ·
              </Typography>
              {statusBadge}
            </Box>
          </Box>
          <ButtonBase
            onClick={() => setShowDetails((value) => !value)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              border: `1px solid ${showDetails ? theme.r4r.navy : theme.r4r.greyLight}`,
              padding: '7px 13px',
              color: showDetails ? theme.r4r.navy : theme.r4r.grey,
              fontFamily: theme.typography.fontFamily,
              fontSize: '11px',
              fontWeight: 600,
              lineHeight: '11px',
              flexShrink: 0
            }}>
            <InfoOutlined sx={{ fontSize: '14px' }} />
            {formatMessage({ id: 'helpdesk.details', defaultMessage: 'Szczegóły' })}
          </ButtonBase>
        </Box>

        {showDetails ? (
          <Box sx={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.3px',
                textTransform: 'uppercase',
                color: theme.r4r.navy,
                mb: '12px'
              }}>
              {formatMessage({
                id: 'helpdesk.faultDescription',
                defaultMessage: 'Opis zgłoszenia'
              })}
            </Typography>
            <Box sx={{ backgroundColor: '#F9FAFB', p: '16px' }}>
              <Typography sx={{ fontSize: '14px', color: theme.r4r.ink, lineHeight: '23.8px' }}>
                {ticket.description}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.3px',
                textTransform: 'uppercase',
                color: theme.r4r.navy,
                mt: '28px',
                mb: '14px'
              }}>
              {formatMessage({ id: 'helpdesk.statusHistory', defaultMessage: 'Historia statusów' })}
            </Typography>
            <FaultTimeline statusCode={ticket.status.code} createdAt={ticket.createdAt} />
          </Box>
        ) : (
          <Box
            ref={chatRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              backgroundColor: theme.r4r.greyBg,
              padding: '20px 28px',
              display: 'flex',
              flexDirection: 'column',
              gap: '18px'
            }}>
            {chatSeparator(
              formatMessage(
                { id: 'helpdesk.reportCreated', defaultMessage: 'Zgłoszenie utworzone · {date}' },
                { date: dayjs(ticket.createdAt).format('DD.MM.YYYY') }
              )
            )}
            {renderBubble({
              key: 'description',
              isMine: true,
              content: ticket.description,
              meta: `${authorName} · ${dayjs(ticket.createdAt).format('DD.MM, HH:mm')}`
            })}
            {ticketsMessagesList.map(({ createdAt, message, author, attachment }) => {
              const isMine = author?.id === ticket.authorPerson.id
              const name = isMine
                ? authorName
                : author?.firstName ??
                  formatMessage({ id: 'defect.serviceR4R', defaultMessage: 'Serwis R4R' })
              return renderBubble({
                key: createdAt,
                isMine,
                content: (
                  <>
                    <MuiMarkdown>{message}</MuiMarkdown>
                    {renderAttachment(attachment)}
                  </>
                ),
                meta: `${name} · ${dayjs(createdAt).format('DD.MM, HH:mm')}`
              })
            })}
          </Box>
        )}

        {canReply && !showDetails && (
          <Box
            sx={{
              borderTop: `1px solid ${theme.r4r.greyLight}`,
              padding: '15px 28px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
            <IconButton
              component='label'
              sx={{
                width: '40px',
                height: '40px',
                borderRadius: 0,
                border: `1px solid ${attachment ? theme.r4r.navy : theme.r4r.greyLight}`,
                color: attachment ? theme.r4r.navy : theme.r4r.grey
              }}>
              <AttachFileOutlined sx={{ fontSize: '20px' }} />
              <input
                hidden
                type='file'
                accept='image/*,.pdf'
                onChange={(event) => {
                  setAttachment(event.target.files?.[0] ?? null)
                  event.target.value = ''
                }}
              />
            </IconButton>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#F9FAFB',
                border: `1px solid ${theme.r4r.greyLight}`
              }}>
              {attachment && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    px: '17px',
                    pt: '8px'
                  }}>
                  <Typography
                    sx={{
                      fontSize: '11px',
                      color: theme.r4r.navy,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                    {attachment.name}
                  </Typography>
                  <IconButton size='small' onClick={() => setAttachment(null)} sx={{ p: 0 }}>
                    <Close sx={{ fontSize: '14px' }} />
                  </IconButton>
                </Box>
              )}
              <InputBase
                value={messageText}
                onChange={(event) => setMessageText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    handleSend()
                  }
                }}
                placeholder={formatMessage({
                  id: 'helpdesk.writeComment',
                  defaultMessage: 'Napisz komentarz...'
                })}
                sx={{ fontSize: '13px', px: '17px', py: '11px' }}
              />
            </Box>
            <IconButton
              onClick={handleSend}
              disabled={(!messageText.trim() && !attachment) || isSending}
              sx={{
                width: '40px',
                height: '40px',
                borderRadius: 0,
                backgroundColor: theme.r4r.navy,
                color: '#fff',
                ':hover': { backgroundColor: theme.r4r.ink },
                ':disabled': { backgroundColor: alpha(theme.r4r.navy, 0.4), color: '#fff' }
              }}>
              <Send sx={{ fontSize: '16px' }} />
            </IconButton>
          </Box>
        )}
      </Box>
    )
  }

  const messagesThread = ticketsMessagesList.length > 0 && (
    <Box>
      {ticketsMessagesList.map(({ createdAt, message, author, attachment }) => {
        const isMine = author?.id === ticket.authorPerson.id
        return (
          <Box
            key={createdAt}
            mt={2}
            sx={{
              display: 'flex',
              alignItems: isMine ? 'flex-end' : 'flex-start',
              flexDirection: 'column'
            }}>
            <Tooltip title={dayjs(createdAt).format('DD/MM/YYYY HH:mm:ss')}>
              <Box
                sx={{
                  backgroundColor: isMine ? alpha(theme.r4r.azure, 0.12) : '#F9FAFB',
                  p: 2,
                  maxWidth: '85%',
                  fontSize: '14px'
                }}>
                <MuiMarkdown>{message}</MuiMarkdown>
                {renderAttachment(attachment)}
              </Box>
            </Tooltip>
            <Typography sx={{ fontSize: '11px', color: theme.r4r.grey, mt: '4px' }}>
              {author?.firstName ??
                formatMessage({ id: 'defect.admin', defaultMessage: 'Administrator' })}
            </Typography>
          </Box>
        )
      })}
    </Box>
  )

  return (
    <>
      <BackNavigationHeader
        label={formatMessage({
          id: 'helpdesk.faultDetails',
          defaultMessage: 'Szczegóły zgłoszenia'
        })}
      />
      <BackNavigationContent>
        <Box
          sx={{
            maxHeight: '80vh',
            overflowY: 'auto',
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'calc(env(safe-area-inset-top) + 6rem)',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
          <Box
            sx={{
              backgroundColor: '#fff',
              boxShadow: theme.r4r.cardShadow,
              p: '16px',
              display: 'flex',
              gap: '14px',
              alignItems: 'flex-start'
            }}>
            <Box
              sx={{
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: getFaultGradient(ticket.faultCategory?.id ?? 0),
                flexShrink: 0
              }}>
              <Icon
                name={ticket.faultCategory?.iconName ?? 'QuestionMark'}
                sx={{ color: '#fff', fontSize: '24px' }}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 700, color: theme.r4r.navy }}>
                {ticket.topic}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, mt: '4px' }}>
                {formatMessage(
                  { id: 'helpdesk.reportedAt', defaultMessage: 'Zgłoszono: {date}' },
                  { date: dayjs(ticket.createdAt).format('DD.MM.YYYY') }
                )}
              </Typography>
            </Box>
            {statusBadge}
          </Box>

          <Box sx={{ backgroundColor: '#fff', boxShadow: theme.r4r.cardShadow, p: '16px' }}>
            <Typography
              sx={{ fontSize: '14px', fontWeight: 700, color: theme.r4r.navy, mb: '10px' }}>
              {formatMessage({
                id: 'helpdesk.faultDescription',
                defaultMessage: 'Opis zgłoszenia'
              })}
            </Typography>
            <Typography sx={{ fontSize: '14px', color: theme.r4r.ink, lineHeight: '22px' }}>
              {ticket.description}
            </Typography>
            {messagesThread}
          </Box>

          <Box sx={{ backgroundColor: '#fff', boxShadow: theme.r4r.cardShadow, p: '16px' }}>
            <Typography
              sx={{ fontSize: '14px', fontWeight: 700, color: theme.r4r.navy, mb: '14px' }}>
              {formatMessage({
                id: 'helpdesk.faultHistory',
                defaultMessage: 'Historia zgłoszenia'
              })}
            </Typography>
            <FaultTimeline statusCode={ticket.status.code} createdAt={ticket.createdAt} />
          </Box>
        </Box>
        {canReportFault && (
          <MobileBottomFooter>
            {ticket.status.code === 'READY' ? (
              <>
                <Typography variant='body2' textAlign='center'>
                  {formatMessage({
                    id: 'defect.resolvedInfo',
                    defaultMessage:
                      'Zgłoszenie zostało rozwiązane i zamknięte. Odpowiadanie nie jest już możliwe. Problem nadal występuje?'
                  })}
                </Typography>
                <Button
                  component={Link}
                  fullWidth
                  sx={{ mt: 2 }}
                  variant='contained'
                  to='/faults/report/add'>
                  {formatMessage({
                    id: 'defect.createNewTicket',
                    defaultMessage: 'Utwórz nowe zgłoszenie'
                  })}
                </Button>
              </>
            ) : (
              <>
                <Typography variant='body2' textAlign='center'>
                  {formatMessage({
                    id: 'defect.replayTime',
                    defaultMessage: 'Wiadomość została przekazana, niebawem wrócimy z odpowiedzią'
                  })}
                </Typography>
                <Button
                  component={Link}
                  fullWidth
                  sx={{ mt: 2 }}
                  variant='contained'
                  to={`/faults/report/${id}/message`}>
                  {formatMessage({
                    id: 'defect.replay',
                    defaultMessage: 'Odpisz'
                  })}
                </Button>
              </>
            )}
          </MobileBottomFooter>
        )}
      </BackNavigationContent>
    </>
  )
}

export default Defect
